import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function BondYieldCalculator() {
  const [faceValue, setFaceValue] = useState("");
  const [couponRate, setCouponRate] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [yearsToMaturity, setYearsToMaturity] = useState("");
  const [result, setResult] = useState<{ currentYield: number; ytm: number } | null>(null);

  const calculate = () => {
    const face = parseFloat(faceValue);
    const coupon = parseFloat(couponRate) / 100;
    const price = parseFloat(currentPrice);
    const years = parseFloat(yearsToMaturity);

    if (isNaN(face) || isNaN(coupon) || isNaN(price) || isNaN(years) || 
        face <= 0 || price <= 0 || years <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    // Current Yield = Annual Coupon Payment / Current Price
    const annualCoupon = face * coupon;
    const currentYield = (annualCoupon / price) * 100;

    // YTM approximation using simplified formula
    const ytm = ((annualCoupon + (face - price) / years) / ((face + price) / 2)) * 100;

    setResult({ currentYield, ytm });
    toast.success("Bond yields calculated!");
  };

  const reset = () => {
    setFaceValue("");
    setCouponRate("");
    setCurrentPrice("");
    setYearsToMaturity("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Bond Yield Calculator</CardTitle>
          <CardDescription>
            Calculate Current Yield and Yield to Maturity (YTM)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="face">Face Value ($)</Label>
            <Input
              id="face"
              type="number"
              placeholder="1000"
              value={faceValue}
              onChange={(e) => setFaceValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="coupon">Annual Coupon Rate (%)</Label>
            <Input
              id="coupon"
              type="number"
              placeholder="5"
              value={couponRate}
              onChange={(e) => setCouponRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="price">Current Bond Price ($)</Label>
            <Input
              id="price"
              type="number"
              placeholder="950"
              value={currentPrice}
              onChange={(e) => setCurrentPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="years">Years to Maturity</Label>
            <Input
              id="years"
              type="number"
              placeholder="10"
              value={yearsToMaturity}
              onChange={(e) => setYearsToMaturity(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Current Yield:</strong> {result.currentYield.toFixed(2)}%
                  </p>
                  <CopyResultButton value={result.currentYield.toFixed(2)} />
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Yield to Maturity:</strong> {result.ytm.toFixed(2)}%
                  </p>
                  <CopyResultButton value={result.ytm.toFixed(2)} />
                </div>
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["bond-yield"]} trigger={result?.ytm} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
